import React, { useState, useEffect, useMemo, useRef } from 'react';
import DataTable from 'react-data-table-component';
import { CSVLink } from 'react-csv';
import { Edit, Trash, ClipboardPlus, Hash } from 'lucide-react';
import Drawer from '../Drawer';
import AddProduct from './AddProduct';
import ProductEdit from './ProductEdit';
import { useNavigate } from 'react-router-dom';
import FiltersPanel from '../FiltersPanel';
import CustomAlert from '../CustomAlert';

const apiUrl = process.env.REACT_APP_API_URL;

export default function ProductList() {
  const [products, setProducts] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const [filter, setFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [manufacturerFilter, setManufacturerFilter] = useState('');
  const [categories, setCategories] = useState([]);
  const [manufacturers, setManufacturers] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [alert, setAlert] = useState({ show: false, message: '', type: 'success' });
  const dropdownRef = useRef(null);

  const handleEditClick = (id) => {
    setEditId(id);
  };

  // Deleting confirms in place — the list stays put, so the row simply
  // disappears once the server has accepted it.
  const handleDeleteClick = (product) => {
    setDeleteTarget(product);
  };

  const allColumns = useMemo(
    () => [
      {
        name: '#',
        selector: (row, index) => index + 1,
        sortable: false,
        width: '60px',
        cell: (row, index) => <span className="font-semibold text-gray-600">{index + 1}</span>,
      },
      {
        name: (
          <div className="flex items-center gap-1">
            <Hash size={14} />
            Код
          </div>
        ),
        selector: (row) => row.code || '',
        sortable: true,
        width: '120px',
        cell: (row) => (
          <div className="flex items-center gap-2">
            <span className="font-mono text-sm text-blue-600">
              {row.code || '-'}
            </span>
            <ClipboardPlus
              size={14}
              className={`cursor-pointer ${
                row.imported ? 'text-green-600' : 'text-gray-400'
              }`}
              title={row.imported ? 'Увезен: Да' : 'Увезен: Не'}
            />
          </div>
        ),
      },
      {
        name: 'Назив',
        selector: (row) => row.name,
        sortable: true,
        grow: 2,
        cell: (row) => <span className="font-semibold text-gray-900">{row.name}</span>,
      },
      {
        name: 'Категорија',
        selector: (row) => row.category_name || '',
        sortable: true,
        width: '170px',
        cell: (row) => <span className="text-gray-700">{row.category_name || '-'}</span>,
      },
      {
        name: 'Производител',
        selector: (row) => row.manufacturer_name || '',
        sortable: true,
        width: '170px',
        cell: (row) => <span className="text-gray-700">{row.manufacturer_name || '-'}</span>,
      },
      {
        name: 'Цена',
        selector: (row) => row.price,
        sortable: true,
        right: true,
        width: '120px',
        cell: (row) => <span className="font-semibold text-gray-900">{row.price}</span>,
      },
      {
        name: 'Набавна цена',
        selector: (row) => row.cost_price || '',
        sortable: true,
        right: true,
        width: '130px',
      },
      {
        name: 'Единечна цена',
        selector: (row) => {
          const price = parseFloat(row.price) || 0;
          const taxPercent = parseFloat(row.tax) || 0;
          const taxAmount = price - price * (100 / (100 + taxPercent));
          return (price - taxAmount).toFixed(2);
        },
        sortable: true,
        width: '130px',
      },
      { name: 'Количина', selector: (row) => row.quantity, sortable: true, width: '110px' },
      { name: 'Тип', selector: (row) => row.type, sortable: true },
      { name: 'Попуст', selector: (row) => row.discount, sortable: true },
      { name: 'Попуст %', selector: (row) => row.discount_percentage, sortable: true },
      { name: 'Опис', selector: (row) => row.description, sortable: false },
      {
        name: 'Данок',
        selector: (row) => `${parseFloat(row.tax).toFixed(0)}%`,
        sortable: true,
        width: '100px',
      },
      { name: 'Локација', selector: (row) => row.location, sortable: true },
      {
        name: 'Креирано на',
        selector: (row) => (row.created_at ? new Date(row.created_at).toLocaleDateString('mk-MK') : ''),
        sortable: true,
        width: '140px',
      },
      {
        name: 'Акции',
        cell: (row) => (
          <div className="flex gap-2 items-center">
            <button
              onClick={() => handleEditClick(row.id)}
              title="Уреди"
              className="p-2 rounded-lg text-indigo-600 hover:bg-indigo-50 transition-colors"
            >
              <Edit size={18} />
            </button>
            <button
              onClick={() => handleDeleteClick(row)}
              title="Избриши"
              className="p-2 rounded-lg text-red-600 hover:bg-red-50 transition-colors"
            >
              <Trash size={18} />
            </button>
          </div>
        ),
        ignoreRowClick: true,
        allowOverflow: true,
        button: true,
        width: '120px',
      },
    ],
    []
  );

  const [visibleColumns, setVisibleColumns] = useState(
    allColumns
      .map((col) => col.name)
      .filter((name) =>
        ['#', 'Назив', 'Категорија', 'Производител', 'Цена', 'Набавна цена', 'Акции'].includes(name)
      )
  );

  const toggleColumn = (columnName) => {
    setVisibleColumns((prev) =>
      prev.includes(columnName) ? prev.filter((name) => name !== columnName) : [...prev, columnName]
    );
  };

  const columns = useMemo(() => {
    return allColumns.filter((col) => visibleColumns.includes(col.name));
  }, [allColumns, visibleColumns]);

  const fetchProducts = () => {
    setLoading(true);
    const token = localStorage.getItem('token');
    // Filtering and search run client-side over what was fetched, so the whole
    // catalogue has to be here — /products otherwise returns only its first
    // page of 20.
    fetch(`${apiUrl}/products?limit=1000`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    })
      .then((res) => res.json())
      .then((data) => setProducts(Array.isArray(data) ? data : []))
      .catch((err) => console.error('Failed to fetch products:', err))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  const confirmDelete = () => {
    if (!deleteTarget || deleting) return;
    setDeleting(true);
    const token = localStorage.getItem('token');

    fetch(`${apiUrl}/products/${deleteTarget.id}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(async (res) => {
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.error || 'Продуктот не е избришан');
        setDeleteTarget(null);
        setAlert({ show: true, type: 'success', message: data.message || 'Продуктот е избришан.' });
        fetchProducts();
      })
      .catch((err) => {
        setDeleteTarget(null);
        setAlert({ show: true, type: 'error', message: err.message || 'Продуктот не е избришан' });
      })
      .finally(() => setDeleting(false));
  };

  // Filter options come from their own endpoints rather than from the loaded
  // products, so every category/manufacturer is offered even when the product
  // list is a single page. /categories paginates (20 by default); /manufacturer
  // does not.
  useEffect(() => {
    const token = localStorage.getItem('token');
    const headers = { Authorization: `Bearer ${token}` };

    fetch(`${apiUrl}/categories?limit=1000`, { headers })
      .then((res) => res.json())
      .then((data) => setCategories(Array.isArray(data) ? data : []))
      .catch((err) => console.error('Failed to fetch categories:', err));

    fetch(`${apiUrl}/manufacturer`, { headers })
      .then((res) => res.json())
      .then((data) => setManufacturers(Array.isArray(data) ? data : []))
      .catch((err) => console.error('Failed to fetch manufacturers:', err));
  }, []);

  const filteredProducts = useMemo(() => {
    const term = filter.trim().toLowerCase();
    return products.filter((p) => {
      const matchesTerm =
        !term ||
        ['name', 'type', 'description', 'location'].some(
          (field) => p[field] && p[field].toString().toLowerCase().includes(term)
        );
      const matchesCategory = !categoryFilter || String(p.category_id) === categoryFilter;
      const matchesManufacturer =
        !manufacturerFilter || String(p.manufacturer_id) === manufacturerFilter;
      return matchesTerm && matchesCategory && matchesManufacturer;
    });
  }, [products, filter, categoryFilter, manufacturerFilter]);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [dropdownRef]);

  if (loading) {
    return (
      <div className="h-full bg-gray-50 flex justify-center items-center">
        <div className="text-center">
          <svg className="animate-spin h-12 w-12 mx-auto text-blue-500 mb-4" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <p className="text-gray-600 text-lg">Вчитување на продукти...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-50">
      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-8">
        {/* Filters Section */}
        <FiltersPanel>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-2 mb-2">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Пребарај</label>
              <input
                type="text"
                placeholder="Назив, тип, опис или локација..."
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                className="w-full px-3 py-1.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Категорија</label>
              <select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                className="w-full px-3 py-1.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm bg-white"
              >
                <option value="">Сите категории</option>
                {categories.map((c) => (
                  <option key={c.id} value={String(c.id)}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Производител</label>
              <select
                value={manufacturerFilter}
                onChange={(e) => setManufacturerFilter(e.target.value)}
                className="w-full px-3 py-1.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm bg-white"
              >
                <option value="">Сите производители</option>
                {manufacturers.map((m) => (
                  <option key={m.id} value={String(m.id)}>
                    {m.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-wrap gap-2 justify-start sm:justify-end">
            <button
              onClick={() => {
                setFilter('');
                setCategoryFilter('');
                setManufacturerFilter('');
              }}
              className="px-3 py-1.5 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 font-medium text-sm transition-colors flex items-center space-x-1.5"
              title="Избриши ги филтрите"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
              <span>Избриши</span>
            </button>
          </div>
                </FiltersPanel>

        {/* Action Bar */}
        <div className="flex flex-wrap gap-2 sm:gap-3 mb-4 sm:mb-6 justify-start sm:justify-end">
          <div className="flex gap-2 items-center flex-wrap justify-start sm:justify-end w-full sm:w-auto">
            <button
              onClick={() => setAddOpen(true)}
              className="w-full sm:w-auto justify-center px-4 py-2.5 bg-green-500 hover:bg-green-600 text-white rounded-lg font-medium text-sm transition-colors flex items-center space-x-2"
              title="Нов продукт"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              <span>Нов Продукт</span>
            </button>
          </div>
        </div>

        {/* Products Table */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden max-w-full">
          <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 border-b border-gray-200">
            <h2 className="text-sm font-semibold text-gray-900">Продукти</h2>
            <div className="flex flex-wrap gap-2 items-center">
              <div className="relative" ref={dropdownRef}>
                <button
                  onClick={() => setShowDropdown(!showDropdown)}
                  className="px-3 py-1.5 bg-white border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 font-medium text-sm transition-colors flex items-center space-x-1.5"
                  title="Избери колони"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h14a1 1 0 011 1v14a1 1 0 01-1 1H5a1 1 0 01-1-1V5z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 4v16M15 4v16" />
                  </svg>
                  <span className="hidden sm:inline">Колони</span>
                </button>

                {showDropdown && (
                  <div className="absolute right-0 w-56 bg-white border border-gray-300 rounded-lg shadow-lg z-50 mt-2">
                    <div className="p-3">
                      <p className="text-xs font-semibold text-gray-600 mb-2 uppercase">Прикажи колони</p>
                      <div className="space-y-2 max-h-64 overflow-auto">
                        {allColumns.map((col) => (
                          <label key={col.name} className="flex items-center cursor-pointer hover:bg-blue-50 p-2 rounded transition-colors">
                            <input
                              type="checkbox"
                              checked={visibleColumns.includes(col.name)}
                              onChange={() => toggleColumn(col.name)}
                              className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-0"
                            />
                            <span className="ml-3 text-sm text-gray-700">{col.name}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <button
                className="px-3 py-1.5 bg-gray-700 hover:bg-gray-800 text-white rounded-md font-medium text-sm transition-colors flex items-center space-x-1.5"
                onClick={fetchProducts}
                disabled={loading}
                title="Освежи ја листата"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                <span className="hidden sm:inline">{loading ? 'Вчитување...' : 'Освежи'}</span>
              </button>

              <button
                onClick={() => navigate('/products/import')}
                className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-md font-medium text-sm transition-colors flex items-center space-x-1.5"
                title="Импортирај продукти"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-8-4l4-4m0 0l4 4m-4-4v9" />
                </svg>
                <span>Импорт</span>
              </button>

              <CSVLink
                data={filteredProducts}
                filename="products.csv"
                target="_blank"
                className="px-3 py-1.5 bg-green-500 hover:bg-green-600 text-white rounded-md font-medium text-sm transition-colors no-underline flex items-center space-x-1.5"
                title="Експортирај ги продуктите во CSV"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                <span className="hidden sm:inline">Експорт</span>
              </CSVLink>
            </div>
          </div>
          <DataTable
            columns={columns}
            data={filteredProducts}
            pagination
            highlightOnHover={false}
            striped={false}
            responsive
            persistTableHead
            noDataComponent={
              <div className="p-12 text-center">
                <svg className="w-12 h-12 mx-auto text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                </svg>
                <p className="text-gray-600">Нема пронајдени продукти</p>
              </div>
            }
          />
        </div>
      </div>

      {/* Wide panel: the form lays out in 3 columns to fill it. */}
      <Drawer open={addOpen} onClose={() => setAddOpen(false)} title="Нов производ" width="max-w-4xl">
        <AddProduct
          onSuccess={() => {
            setAddOpen(false);
            fetchProducts();
          }}
        />
      </Drawer>

      <Drawer open={editId !== null} onClose={() => setEditId(null)} title="Измени производ" width="max-w-4xl">
        {/* key remounts the form when another row is picked, so it refetches. */}
        <ProductEdit
          key={editId}
          id={editId}
          onSuccess={() => {
            setEditId(null);
            fetchProducts();
          }}
        />
      </Drawer>

      {/* Delete confirmation — replaces the old /products/delete/:id page. */}
      <CustomAlert
        isOpen={deleteTarget !== null}
        type="error"
        title="Избриши продукт"
        message={`Дали сте сигурни дека сакате да го избришете „${deleteTarget?.name ?? ''}“?`}
        onClose={() => setDeleteTarget(null)}
        onConfirm={confirmDelete}
        showConfirmButton={true}
        confirmText={deleting ? 'Се брише...' : 'Избриши'}
        cancelText="Откажи"
      />

      <CustomAlert
        isOpen={alert.show}
        type={alert.type}
        title={alert.type === 'success' ? 'Успешно' : 'Грешка'}
        message={alert.message}
        onClose={() => setAlert({ show: false, message: '', type: 'success' })}
      />
    </div>
  );
}
